import { createAsyncThunk } from "@reduxjs/toolkit";
import { storageApi } from "./api";
import {
  capitalize,
  generateParametersFromDownloadJSONQueryFromS3Url,
  s3Prefix,
} from "../../util/functions";
import { ROUTE_KEYS, ROUTES } from "../../_configuration/routing/constants";
import {
  initializeAdapter,
  selectAllowedToDownloadFromNonPublicS3,
  selectFormInfoForS3Url,
  setReferenceKey,
} from "./slices";
import {
  selectActiveReportingPeriodS3Map,
  setActiveSessionPaths,
} from "../submissionPeriod/slices";
import { sliceNames } from "../util/constants";

function formatFieldName(fieldName) {
  return (
    fieldName
      // Insert space before uppercase letters (except the first character)
      .replace(/([a-z])([A-Z])/g, "$1 $2")
      // Capitalize the first letter of each word
      .replace(/\b\w/g, (letter) => letter.toUpperCase())
  );
}

export const syncReduxWithS3 = createAsyncThunk(
  `${sliceNames.storage}/initializeJSONForCurrentSession`,
  async (
    parms = {},
    { dispatch, getState, rejectWithValue, fulfillWithValue }
  ) => {
    const { formType = "", s3Path = "", formId } = parms
    try {
      if(formId && s3Path) {
        dispatch(setReferenceKey({
          key: formId,
          value: s3Path
        }))
      }
      let destination, heading;
      const downloadJsonDataParams =
        generateParametersFromDownloadJSONQueryFromS3Url(s3Path);
      const { isUninitialized, data: cachedData } = selectFormInfoForS3Url(
        getState(),
        {
          s3Url: s3Path,
        }
      );
      
      let relatedJsonData = cachedData;
      const canAccessS3 = selectAllowedToDownloadFromNonPublicS3(getState())
      if (canAccessS3 && isUninitialized) {
        const downloadJsonDataInitiator =
          storageApi.endpoints.downloadJsonData.initiate(
            downloadJsonDataParams
          );
        relatedJsonData = await dispatch(downloadJsonDataInitiator).unwrap();
      }
      const questions = relatedJsonData?.questions || [];

      heading =
        relatedJsonData?.heading ||
        formatFieldName(formType) ||
        capitalize(formType);
      const rootPath = ROUTES?.[ROUTE_KEYS.FORMS].path;
      if (rootPath && heading && formType) {
        destination = {
          path: `${rootPath.startsWith("/") ? rootPath : "/" + rootPath}/${formType}`,
          title: heading,
          formType,
          s3Path,
        };
      }
      
      return fulfillWithValue({
        destination,
        questions,
        heading,
      });
    } catch (error) {
      console.error(error);
      return rejectWithValue(error?.message);
    }
  }
);

export const initializeJSONForCurrentSession = createAsyncThunk(
  `${sliceNames.storage}/initializeJSONForCurrentSession`,
  async (
    params = {},
    { dispatch, getState, rejectWithValue, fulfillWithValue }
  ) => {
    const state = getState();
    try {
      const s3Map = selectActiveReportingPeriodS3Map(state);
      const s3RelatedEntries = Object.entries(s3Map || {}).filter(
        ([key, value]) => value.startsWith(s3Prefix) && value.endsWith(".json")
      );
      if(!s3RelatedEntries?.length) {
        return rejectWithValue(`No reporting period identified`);
      }
      s3RelatedEntries.forEach(([formType, jsonToDownload]) => {
        dispatch(
          initializeAdapter({
            s3Path: jsonToDownload,
            formType,
          })
        );
      });
      const availablePaths = Promise.allSettled(
        s3RelatedEntries.map(([formType, s3Path]) => {
          return dispatch(
            syncReduxWithS3({
              s3Path,
              formType,
            })
          ).unwrap()
        })
      );

      let paths = []
      try {
        const response = await availablePaths
        response.forEach(({ value }) => {
          const { destination } = value
          if(destination?.path && destination?.title) {
            paths.push(destination)
          }
        })
      } catch (error) {
        return rejectWithValue(`Could not properly construct paths`);
      }
      dispatch(setActiveSessionPaths(paths));
      return fulfillWithValue({ paths: paths });
    } catch (error) {
      console.error(error);
      return rejectWithValue(error?.message);
    }
  }
);
